import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Plus, X, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card, Field, ImageUpload, BigButton } from "@/components/admin/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const TEXT = [["brand_name", "Brand name (website)"], ["company_name", "Company legal name (invoices)"], ["npwp", "NPWP"],
  ["email", "Email"], ["phone", "Phone"], ["whatsapp_link", "WhatsApp link"], ["instagram", "Instagram URL"], ["instagram_handle", "Instagram username (shown)"], ["facebook", "Facebook URL"], ["facebook_name", "Facebook name (shown)"]];

const Section = ({ title, children }) => (
  <Card className="p-4 sm:p-6 space-y-4"><p className="font-extrabold text-lg">{title}</p>{children}</Card>
);

export default function Settings() {
  const { user } = useOutletContext();
  const [s, setS] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-slate-500">Loading…</p>;
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/settings", { ...s, umkm_rate: Number(s.umkm_rate) || 0.5, invoice_due_days: Number(s.invoice_due_days) || 7 });
      setS(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const addEmail = () => {
    const e = email.trim().toLowerCase();
    if (!e.includes("@") || s.admin_emails.includes(e)) return;
    setS({ ...s, admin_emails: [...s.admin_emails, e] });
    setEmail("");
  };

  return (
    <div className="max-w-3xl space-y-4 pb-10" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Company details, invoice text, tax mode and who can sign in." />
      <Section title="Company & contact">
        <Field label="Logo (shown on website, admin and invoices)"><ImageUpload value={s.logo_url} onChange={set("logo_url")} testid="settings-logo-upload" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          {TEXT.map(([k, l]) => <Field key={k} label={l}><Input value={s[k] || ""} onChange={set(k)} className="h-12" data-testid={`settings-${k}-input`} /></Field>)}
        </div>
        <Field label="Address"><Textarea value={s.address || ""} onChange={set("address")} data-testid="settings-address-input" /></Field>
      </Section>
      <Section title="Invoices">
        <Field label="Bank details printed on invoices"><Textarea value={s.bank_details || ""} onChange={set("bank_details")} data-testid="settings-bank-details-input" /></Field>
        <Field label="Default days until due"><Input type="number" value={s.invoice_due_days} onChange={set("invoice_due_days")} className="h-12 w-32" data-testid="settings-due-days-input" /></Field>
        <Field label="Terms & Conditions (added as extra pages to every invoice PDF)"><Textarea rows={10} value={s.terms || ""} onChange={set("terms")} data-testid="settings-terms-input" /></Field>
      </Section>
      <Section title="Pajak (tax mode)">
        <RadioGroup value={s.tax_mode} onValueChange={set("tax_mode")} className="space-y-2">
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer"><RadioGroupItem value="umkm" data-testid="settings-tax-umkm" className="mt-1" />
            <span><b>UMKM — PPh Final {String(s.umkm_rate).replace(".", ",")}% (PP 55/2022)</b><br /><span className="text-sm text-slate-500">Monthly gross income recap and PPh Final due.</span></span></label>
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer"><RadioGroupItem value="regular" data-testid="settings-tax-regular" className="mt-1" />
            <span><b>PPN & PPh (regular)</b><br /><span className="text-sm text-slate-500">Tax fields (DPP, PPN, PPh, NPWP) open by default on every entry. Full PPN reports come in phase 2.</span></span></label>
        </RadioGroup>
        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <Field label="UMKM rate (%)"><Input type="number" step="0.1" value={s.umkm_rate} onChange={set("umkm_rate")} className="h-12" data-testid="settings-umkm-rate-input" /></Field>
          <label className="flex items-center justify-between gap-3 text-sm font-semibold rounded-xl border border-border p-3">Wajib Pajak Orang Pribadi (omzet s.d. Rp 500 juta bebas)<Switch checked={!!s.wp_orang_pribadi} onCheckedChange={set("wp_orang_pribadi")} data-testid="settings-wp-op-switch" /></label>
        </div>
        <Link to="/admin/finance?tab=akun" className="inline-flex items-center gap-2 text-sm font-semibold text-oceandeep" data-testid="settings-bank-accounts-link"><Landmark className="w-4 h-4" />Manage bank & cash accounts (Daftar Akun)</Link>
      </Section>
      <Section title="Approved admin emails">
        <p className="text-sm text-slate-500">Only these Google accounts can open the dashboard.</p>
        {s.admin_emails.map((e) => (
          <div key={e} className="flex items-center justify-between rounded-xl bg-sand px-4 h-12" data-testid={`admin-email-${e}`}>
            <span className="font-semibold">{e}</span>
            {e !== user.email.toLowerCase() && <button onClick={() => setS({ ...s, admin_emails: s.admin_emails.filter((x) => x !== e) })} className="p-1 text-slate-500 hover:text-rose-600" aria-label="Remove" data-testid={`remove-admin-email-${e}`}><X className="w-4 h-4" /></button>}
          </div>
        ))}
        <div className="flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" className="h-12" data-testid="add-admin-email-input" />
          <button onClick={addEmail} className="h-12 w-12 rounded-xl bg-ocean text-white grid place-items-center shrink-0" aria-label="Add email" data-testid="add-admin-email-btn"><Plus /></button>
        </div>
      </Section>
      <div className="sticky bottom-20 lg:bottom-4"><BigButton onClick={save} disabled={busy} className="w-full shadow-xl" data-testid="settings-save-btn">{busy ? "Saving…" : "Save settings"}</BigButton></div>
    </div>
  );
}
